/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { isRightToLeft } from 'js/lib/language';

import { ButtonBase, VisuallyHidden, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ChevronNextIcon, ChevronPreviousIcon } from '@coursera/cds-icons';

import NavigationButtons from 'bundles/cds-labs/components/OverflowCarousel/NavigationButtons';
import OverflowCarouselProvider, {
  useGetItem,
  useItemsOffset,
  useRegisterItem,
} from 'bundles/cds-labs/components/OverflowCarousel/OverflowCarouselProvider';
import { CDS_GRID_LARGE_PADDING, CDS_GRID_PADDING } from 'bundles/cds-labs/components/OverflowCarousel/constants';
import navigateToItem from 'bundles/cds-labs/components/OverflowCarousel/navigateToItem';
import scrollToNext from 'bundles/cds-labs/components/OverflowCarousel/scrollToNext';
import scrollToPrevious from 'bundles/cds-labs/components/OverflowCarousel/scrollToPrevious';
import useUpdateOverflowCarouselState from 'bundles/cds-labs/components/OverflowCarousel/useUpdateOverflowCarouselState';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

import _t from 'i18n!nls/cds-labs';

export type Props = {
  className?: string;
  // when true, the overflow carousel will have extra padding to allow for items with zoom-in hover effect,
  // so that the items are not cut off by the container div.
  shouldAllowSpaceForHoverItems?: boolean;
  // when true, the overflow carousel will show navigation buttons below the carousel.
  showNavigationButtons?: boolean;
  // when true, the overflow carousel will start overflowing at the medium breakpoint.
  shouldStartOverflowAtMediumBreakpoint?: boolean;
};

// var(--cds-spacing-100), which is 8px. This is to add room for items with zoom-in hover effect,
// so that the items are not cut off by the container div.
const VERTICAL_PADDING = 8;

const NAVIGATION_BUTTONS_HEIGHT = 24;

const styles = {
  container: css`
    display: flex;
    align-items: center;
    position: relative;

    /* 
     * using the negative margin to offset the padding of the CDS grid
     * to extend the overflow carousel to the edges of the browser window 
    */
    margin-right: calc(${CDS_GRID_PADDING}px * -1);
    margin-left: calc(${CDS_GRID_PADDING}px * -1);
  `,
  containerMedium: css`
    ${breakpoints.only('md')} {
      margin-right: calc(${CDS_GRID_LARGE_PADDING}px * -1);
      margin-left: calc(${CDS_GRID_LARGE_PADDING}px * -1);
    }
  `,
  addBottomPaddingForNavigationButtons: css`
    padding-bottom: ${NAVIGATION_BUTTONS_HEIGHT - VERTICAL_PADDING}px;
  `,
  addExtraBottomPaddingForNavigationButtons: css`
    padding-bottom: ${NAVIGATION_BUTTONS_HEIGHT}px;
  `,
  overflowContainerWithSpaceForHoverItems: css`
    /* 
     * in additon to the left and right negative margins to offset the padding of the CDS grid,
     * we also need to add negative margins to add room for items with zoom-in hover effect,
     * so that the items are not cut off by the container div.
    */
    margin-top: calc(${VERTICAL_PADDING}px * -1);
    margin-bottom: calc(${VERTICAL_PADDING}px * -1);
  `,
  button: (fullHeight: boolean) => css`
    border-radius: 0;
    height: ${fullHeight ? `calc(100% - ${VERTICAL_PADDING}px * 2)` : '100%'};
    overflow: hidden;

    svg {
      display: flex;
      line-height: 1;
    }

    &:hover {
      color: var(--cds-color-interactive-primary);
    }
  `,
  previousButton: css`
    position: absolute;
    padding: 0 var(--cds-spacing-50) 0 0;
    left: 0;
    z-index: 1;
    background: linear-gradient(270deg, var(--cds-color-lighten-0) 0%, var(--cds-color-lighten-800) 80%);

    svg {
      margin-left: calc(var(--cds-spacing-50) * -1);
    }

    &:hover {
      background: linear-gradient(
        90deg,
        var(--cds-color-neutral-primary-invert) 0%,
        var(--cds-color-lighten-800) 60%,
        var(--cds-color-lighten-100) 100%
      );
    }
  `,
  previousButtonRTL: css`
    padding: 0 0 0 var(--cds-spacing-50);
    background: linear-gradient(90deg, var(--cds-color-lighten-0) 0%, var(--cds-color-lighten-800) 80%);

    &:hover {
      background: linear-gradient(
        90deg,
        var(--cds-color-lighten-100) 0%,
        var(--cds-color-lighten-800) 40%,
        var(--cds-color-neutral-primary-invert) 100%
      );
    }
  `,
  nextButton: css`
    position: absolute;
    padding: 0 0 0 var(--cds-spacing-50);
    right: 0;
    z-index: 1;
    background: linear-gradient(90deg, var(--cds-color-lighten-0) 0%, var(--cds-color-lighten-800) 80%);

    svg {
      margin-right: calc(var(--cds-spacing-50) * -1);
    }

    &:hover {
      background: linear-gradient(
        90deg,
        var(--cds-color-lighten-100) 0%,
        var(--cds-color-lighten-800) 40%,
        var(--cds-color-neutral-primary-invert) 100%
      );
    }
  `,
  nextButtonRTL: css`
    padding: 0 var(--cds-spacing-50) 0 0;
    background: linear-gradient(90deg, var(--cds-color-lighten-800) 80%, var(--cds-color-lighten-0) 100%);

    &:hover {
      background: linear-gradient(
        90deg,
        var(--cds-color-neutral-primary-invert) 0%,
        var(--cds-color-lighten-800) 60%,
        var(--cds-color-lighten-100) 100%
      );
    }
  `,
  carousel: css`
    display: flex;
    width: 100%;
    align-items: center;
    position: relative;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-behavior: smooth;
    scroll-snap-stop: always;
    scroll-snap-align: start;
    min-height: 36px;

    /* hide scrollbar for Chrome, Safari and Edge */
    ::-webkit-scrollbar {
      display: none;
    }
  `,
  carouselWithSpaceForHoverItems: css`
    padding: ${VERTICAL_PADDING}px 0;
  `,
  contentWrapper: css`
    padding-left: ${CDS_GRID_PADDING}px;
    padding-right: ${CDS_GRID_PADDING}px;
  `,
  contentWrapperMedium: css`
    ${breakpoints.only('md')} {
      padding-left: ${CDS_GRID_LARGE_PADDING}px;
      padding-right: ${CDS_GRID_LARGE_PADDING}px;
    }
  `,
  visuallyHidden: css`
    bottom: 0;
  `,
};

/**
 * This component acts as a container for multiple list item elements (such as product cards, tags, etc.)
 * and handle overflow behavior so that the items don't fall down to the next line.
 */
const OverflowCarouselInner = (props: React.PropsWithChildren<Props>) => {
  const {
    children,
    className,
    shouldAllowSpaceForHoverItems,
    showNavigationButtons: showNavigationButtonsProp,
    shouldStartOverflowAtMediumBreakpoint,
  } = props;
  const userAgent = useUserAgent();
  const isMobileBrowser = userAgent?.isMobileBrowser;
  const isMediumBreakpoint = useMediaQuery(breakpoints.only('md'));
  const offset =
    isMediumBreakpoint && shouldStartOverflowAtMediumBreakpoint ? CDS_GRID_LARGE_PADDING : CDS_GRID_PADDING;
  const carouselRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const registerItem = useRegisterItem();
  const isRTL = isRightToLeft(_t.getLocale());
  const getItem = useGetItem();
  const getItemsOffset = useItemsOffset();
  const showNavigationButtons = !!showNavigationButtonsProp && !isMobileBrowser;

  const [showNextButton, setShowNextButton] = useState<boolean>(false);
  const [showPreviousButton, setShowPreviousButton] = useState<boolean>(false);
  const [itemsInView, setItemsInView] = useState<string[]>([]);
  const [focusedItem, setFocusedItem] = useState<string | null>(null);
  const previousButtonConditionalStyle = {
    display: showPreviousButton ? 'block' : 'none',
  };

  const nextButtonConditionalStyle = {
    display: showNextButton ? 'block' : 'none',
  };

  const updateCarouselState = useUpdateOverflowCarouselState({
    setShowPreviousButton,
    setShowNextButton,
    isMobileBrowser: !!isMobileBrowser,
    setItemsInView,
  });

  useEffect(() => {
    const carouselRefCurrent = carouselRef.current;
    const contentRefCurrent = contentRef.current;

    updateCarouselState(carouselRefCurrent, contentRefCurrent);

    const handleResize = debounce(() => {
      updateCarouselState(carouselRefCurrent, contentRefCurrent);
    }, 100);

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    const handleScroll = debounce(() => {
      updateCarouselState(carouselRefCurrent, contentRefCurrent);
    }, 100);

    carouselRefCurrent?.addEventListener('scroll', handleScroll);

    return () => {
      carouselRefCurrent?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateCarouselState]);

  const onClickNext = () => {
    const carouselRefCurrent = carouselRef.current;

    if (!carouselRefCurrent) {
      return;
    }

    const itemsOffset = getItemsOffset?.() ?? new Map<string, { offsetLeft: number; offsetWidth: number }>();

    scrollToNext({ carouselRefCurrent, itemsOffset, isRightToLeft: isRTL, offset });
  };

  const onClickPrevious = () => {
    const carouselRefCurrent = carouselRef.current;

    if (!carouselRefCurrent) {
      return;
    }
    const itemsOffset = getItemsOffset?.() ?? new Map<string, { offsetLeft: number; offsetWidth: number }>();

    scrollToPrevious({ carouselRefCurrent, itemsOffset, isRightToLeft: isRTL, offset });
  };

  const goToItem = (index: number) => () => {
    const carouselRefCurrent = carouselRef.current;

    if (!carouselRefCurrent) {
      return;
    }

    const itemsOffset = getItemsOffset?.() ?? new Map<string, { offsetLeft: number; offsetWidth: number }>();

    navigateToItem({
      index,
      carouselRefCurrent,
      itemsOffset,
      isRightToLeft: isRTL,
      offset,
    });

    setFocusedItem(index.toString());
  };

  useEffect(() => {
    const carouselRefCurrent = carouselRef.current;

    if (!showNavigationButtons) {
      return () => {};
    }

    const setFocus = () => {
      if (focusedItem !== null) {
        const itemElement = getItem?.(focusedItem);
        itemElement?.focus();
        setFocusedItem(null);
      }
    };

    if (
      (carouselRefCurrent?.scrollLeft === 0 && focusedItem === '0') ||
      (!showNextButton && focusedItem && itemsInView.includes(focusedItem))
    ) {
      setFocus();
    }

    carouselRefCurrent?.addEventListener('scrollend', setFocus);
    return () => {
      carouselRefCurrent?.removeEventListener('scrollend', setFocus);
    };
  }, [focusedItem, getItem, showNavigationButtons, showNextButton, itemsInView]);

  return (
    <div
      css={[
        styles.container,
        shouldAllowSpaceForHoverItems && styles.overflowContainerWithSpaceForHoverItems,
        showNavigationButtons && !shouldAllowSpaceForHoverItems && styles.addExtraBottomPaddingForNavigationButtons,
        showNavigationButtons && shouldAllowSpaceForHoverItems && styles.addBottomPaddingForNavigationButtons,
        shouldStartOverflowAtMediumBreakpoint && styles.containerMedium,
      ]}
      data-testid="overflow-carousel"
    >
      <div
        css={[styles.carousel, shouldAllowSpaceForHoverItems && styles.carouselWithSpaceForHoverItems]}
        ref={carouselRef}
      >
        <div
          className={className}
          ref={contentRef}
          data-testid="overflow-carousel-content"
          role="list"
          css={[styles.contentWrapper, shouldStartOverflowAtMediumBreakpoint && styles.contentWrapperMedium]}
        >
          {React.Children.map(children, (child, index) => {
            return (
              <div
                role="listitem"
                ref={(node) => {
                  if (node && index !== undefined) {
                    registerItem?.(index.toString(), node);
                  }
                }}
                {...(showNavigationButtons ? { tabIndex: -1 } : {})}
              >
                {child}
              </div>
            );
          })}
        </div>
      </div>
      <ButtonBase
        css={[
          styles.button(!!shouldAllowSpaceForHoverItems),
          styles.previousButton,
          isRTL ? styles.previousButtonRTL : undefined,
          previousButtonConditionalStyle,
        ]}
        onClick={onClickPrevious}
        tabIndex={-1}
      >
        <ChevronPreviousIcon size="large" />
        <VisuallyHidden>{_t('Previous')}</VisuallyHidden>
      </ButtonBase>
      <ButtonBase
        css={[
          styles.button(!!shouldAllowSpaceForHoverItems),
          styles.nextButton,
          isRTL ? styles.nextButtonRTL : undefined,
          nextButtonConditionalStyle,
        ]}
        onClick={onClickNext}
        tabIndex={-1}
      >
        <ChevronNextIcon size="large" />
        <VisuallyHidden>{_t('Next')}</VisuallyHidden>
      </ButtonBase>
      {showNavigationButtons && (
        <NavigationButtons
          numberOfItems={React.Children.count(children)}
          navigateToItem={goToItem}
          itemsInView={itemsInView}
          indentToAlignWithGridAtMediumBreakpoint={shouldStartOverflowAtMediumBreakpoint}
        />
      )}
      {(showNextButton || showPreviousButton) && itemsInView.length > 0 && (
        <VisuallyHidden css={styles.visuallyHidden}>
          <div aria-live="polite" aria-atomic="true">
            {itemsInView.length > 2 ? (
              <FormattedMessage
                message={_t('Displaying items #{start} to #{end}, out of a total of {total} items.')}
                start={Number(itemsInView[0]!) + 1}
                end={Number(itemsInView[itemsInView.length - 1]!) + 1}
                total={React.Children.count(children)}
              />
            ) : (
              <FormattedMessage
                message={_t(
                  'Displaying {count, plural, =1 {item} other {items}} {items}, out of a total of {total} items.'
                )}
                items={itemsInView.map((item) => `#${Number(item) + 1}`).join(', ')}
                total={React.Children.count(children)}
                count={itemsInView.length}
              />
            )}
          </div>
        </VisuallyHidden>
      )}
    </div>
  );
};

const OverflowCarousel = (props: React.PropsWithChildren<Props>) => {
  return (
    <OverflowCarouselProvider>
      <OverflowCarouselInner {...props} />
    </OverflowCarouselProvider>
  );
};

export default OverflowCarousel;
